import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertCircle, Loader2, Trophy, Shield, Building2, Calendar, Home, Users, ChevronDown, ChevronUp, Settings, Landmark, KeyRound } from 'lucide-react';
import axios from 'axios';
import ChangePasswordDialog from '../components/ChangePasswordDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Core demo users (non-coach)
const SEMPRION_DEMO = {
  email: 'semprion@demo.com', label: 'Semprion HQ', icon: Shield,
  color: 'bg-red-600 hover:bg-red-700', description: 'Super User · /admin'
};
const CORE_DEMO_USERS = [
  { email: 'admin@omnileague.com', label: 'Admin', icon: Shield, color: 'bg-red-600 hover:bg-red-700', description: 'Full system access' },
  { email: 'league.admin@demo.com', label: 'League', icon: Trophy, color: 'bg-amber-600 hover:bg-amber-700', description: 'League management' },
  { email: 'athletic.director@demo.com', label: 'AD', icon: Building2, color: 'bg-blue-600 hover:bg-blue-700', description: 'Athletic Director' },
  { email: 'scheduler@demo.com', label: 'Scheduler', icon: Calendar, color: 'bg-purple-600 hover:bg-purple-700', description: 'Game scheduling' },
  { email: 'tech@demo.com', label: 'Technology Specialist', icon: Settings, color: 'bg-teal-600 hover:bg-teal-700', description: 'Website, streaming video, social media' },
  { email: 'treasurer@demo.com', label: 'Treasurer', icon: Landmark, color: 'bg-emerald-600 hover:bg-emerald-700', description: 'OL Finance — books, reports' },
  { email: 'family@demo.com', label: 'Family', icon: Home, color: 'bg-cyan-600 hover:bg-cyan-700', description: 'Player registration' },
];

const DEMO_PASSWORD = 'Demo2026!';

export default function DevAuthPage() {
  const { login, register, forgotPassword, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCoaches, setShowCoaches] = useState(false);
  const [demoCoaches, setDemoCoaches] = useState([]);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [registerData, setRegisterData] = useState({
    email: '', password: '', confirmPassword: '', first_name: '', last_name: '', phone: '', access_code: ''
  });

  const [forgotEmail, setForgotEmail] = useState('');
  const [showForgot, setShowForgot] = useState(false);

  // Fetch demo coaches dynamically
  useEffect(() => {
    axios.get(`${API_URL}/api/coaches/demo-coaches`)
      .then(res => setDemoCoaches(res.data))
      .catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try { localStorage.setItem('ol_dev_session', 'true'); } catch {}
    const result = await login(loginEmail, loginPassword);
    if (!result.success) setError(result.error);
    else _navigateByRole(result.user);
    setLoading(false);
  };

  const handleDemoLogin = async (email) => {
    setError('');
    setDemoLoading(email);
    try { localStorage.setItem('ol_dev_session', 'true'); } catch {}
    const result = await login(email, DEMO_PASSWORD);
    if (!result.success) setError(result.error);
    else _navigateByRole(result.user);
    setDemoLoading(null);
  };

  const _navigateByRole = (u) => {
    // After signing in through /dev, route semprion_admin accounts to
    // /admin, everyone else to /dashboard. Navigation is deferred so the
    // auth cookie has time to propagate for subsequent requests.
    const isDev = !!u?.roles?.some(r => r.role_type === 'developer');
    const isSemprion = !!u?.roles?.some(r => r.role_type === 'semprion_admin');
    const multi = (u?.roles?.length || 0) > 1 && !u?.active_role;
    setTimeout(() => {
      navigate(isDev ? '/dev' : multi ? '/select-role' : isSemprion ? '/admin' : '/dashboard',
        { replace: true });
    }, 150);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (registerData.password !== registerData.confirmPassword) { setError('Passwords do not match'); setLoading(false); return; }
    if (registerData.password.length < 8) { setError('Password must be at least 8 characters'); setLoading(false); return; }
    const code = (registerData.access_code || '').trim();
    if (!code) { setError('Access code is required'); setLoading(false); return; }
    const result = await register({
      email: registerData.email, password: registerData.password,
      first_name: registerData.first_name, last_name: registerData.last_name, phone: registerData.phone || null,
      access_code: code,
    });
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    const result = await forgotPassword(forgotEmail);
    if (result.success) { setSuccess(result.message); setForgotEmail(''); }
    else { setError(result.error); }
    setLoading(false);
  };

  if (showForgot) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Trophy className="h-8 w-8 text-amber-500" />
              <span className="text-2xl font-bold text-white">OmniLeagues</span>
            </div>
            <CardTitle className="text-white">Reset Password</CardTitle>
            <CardDescription className="text-slate-400">Enter your email to receive a reset link</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              {error && <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400" data-testid="forgot-error"><AlertCircle className="h-4 w-4" /><span className="text-sm">{error}</span></div>}
              {success && <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-3 text-green-400 text-sm" data-testid="forgot-success">{success}</div>}
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-slate-300">Email</Label>
                <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="your@email.com" required className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="forgot-email-input" />
              </div>
              <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-white" disabled={loading} data-testid="forgot-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Reset Link'}</Button>
              <Button type="button" variant="ghost" className="w-full text-slate-400 hover:text-white" onClick={() => setShowForgot(false)} data-testid="back-to-login-btn">Back to Login</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl flex flex-col lg:flex-row gap-6">
        {/* Main Auth Card */}
        <Card className="flex-1 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Trophy className="h-8 w-8 text-amber-500" />
              <span className="text-2xl font-bold text-white">OmniLeagues</span>
            </div>
            <CardTitle className="text-white">Welcome</CardTitle>
            <CardDescription className="text-slate-400">Sports League Management Platform</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 bg-slate-700/50">
                <TabsTrigger value="login" className="data-[state=active]:bg-amber-600" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-amber-600" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-4">
                <form onSubmit={handleLogin} className="space-y-4">
                  {error && <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400" data-testid="login-error"><AlertCircle className="h-4 w-4" /><span className="text-sm">{error}</span></div>}
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                    <Input id="login-email" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="your@email.com" required className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="login-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                    <Input id="login-password" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="Enter your password" required className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="login-password-input" />
                  </div>
                  <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-white" disabled={loading} data-testid="login-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}</Button>
                  <Button type="button" variant="link" className="w-full text-amber-500 hover:text-amber-400" onClick={() => setShowForgot(true)} data-testid="forgot-password-link">Forgot password?</Button>
                </form>
              </TabsContent>

              <TabsContent value="register" className="mt-4">
                <form onSubmit={handleRegister} className="space-y-4">
                  {error && <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400" data-testid="register-error"><AlertCircle className="h-4 w-4" /><span className="text-sm">{error}</span></div>}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="text-slate-300">First Name</Label>
                      <Input id="first_name" value={registerData.first_name} onChange={(e) => setRegisterData({...registerData, first_name: e.target.value})} placeholder="John" required className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-firstname-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name" className="text-slate-300">Last Name</Label>
                      <Input id="last_name" value={registerData.last_name} onChange={(e) => setRegisterData({...registerData, last_name: e.target.value})} placeholder="Doe" required className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-lastname-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-slate-300">Email</Label>
                    <Input id="register-email" type="email" value={registerData.email} onChange={(e) => setRegisterData({...registerData, email: e.target.value})} placeholder="your@email.com" required className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-300">Phone (Optional)</Label>
                    <Input id="phone" type="tel" value={registerData.phone} onChange={(e) => setRegisterData({...registerData, phone: e.target.value})} placeholder="919-555-0100" className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-phone-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-slate-300">Password</Label>
                    <Input id="register-password" type="password" value={registerData.password} onChange={(e) => setRegisterData({...registerData, password: e.target.value})} placeholder="Min 8 characters" required className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-slate-300">Confirm Password</Label>
                    <Input id="confirm-password" type="password" value={registerData.confirmPassword} onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})} placeholder="Confirm your password" required className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-confirm-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="access-code" className="text-slate-300">Access Code <span className="text-amber-400">(required)</span></Label>
                    <Input id="access-code" value={registerData.access_code} onChange={(e) => setRegisterData({...registerData, access_code: e.target.value.toUpperCase()})} placeholder="Enter your access code" required className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-access-code-input" />
                    <p className="text-[11px] text-slate-500">Closed beta — only authorized codes work.</p>
                  </div>
                  <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-white" disabled={loading} data-testid="register-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}</Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Quick Demo Access Card */}
        <Card className="w-full lg:w-80 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg">Quick Demo Access</CardTitle>
            <CardDescription className="text-slate-400 text-xs">Click any role to sign in instantly</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Super User (Semprion HQ) — always on top; routes to /admin after login.
                Part of the AUTH_SPEC developer quick-access pattern. */}
            {(() => {
              const Icon = SEMPRION_DEMO.icon;
              const isLoading = demoLoading === SEMPRION_DEMO.email;
              return (
                <Button onClick={() => handleDemoLogin(SEMPRION_DEMO.email)} disabled={demoLoading !== null}
                  className={`w-full justify-start gap-3 ${SEMPRION_DEMO.color} text-white h-auto py-2`}
                  data-testid="demo-semprion-btn">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                  <div className="text-left">
                    <div className="font-medium">{SEMPRION_DEMO.label}</div>
                    <div className="text-xs opacity-80">{SEMPRION_DEMO.description}</div>
                  </div>
                </Button>
              );
            })()}

            {/* Core Roles */}
            {CORE_DEMO_USERS.map((user) => {
              const Icon = user.icon;
              const isLoading = demoLoading === user.email;
              return (
                <Button key={user.email} onClick={() => handleDemoLogin(user.email)} disabled={demoLoading !== null}
                  className={`w-full justify-start gap-3 ${user.color} text-white h-auto py-2`}
                  data-testid={`demo-${user.label.toLowerCase()}-btn`}>
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                  <div className="text-left">
                    <div className="font-medium">{user.label}</div>
                    <div className="text-xs opacity-80">{user.description}</div>
                  </div>
                </Button>
              );
            })}

            {/* Coach Section — Expandable */}
            <div className="pt-1">
              <Button variant="ghost" onClick={() => setShowCoaches(!showCoaches)}
                className="w-full justify-between text-slate-300 hover:text-white h-8 px-2" data-testid="toggle-coaches-btn">
                <span className="flex items-center gap-2 text-sm">
                  <Users className="h-3.5 w-3.5" />Team Coaches ({demoCoaches.length})
                </span>
                {showCoaches ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </Button>

              {showCoaches && (
                <div className="space-y-1 mt-1 max-h-52 overflow-y-auto pr-1">
                  {demoCoaches.map((coach) => {
                    const isLoading = demoLoading === coach.email;
                    const isHead = coach.coach_type === 'head';
                    return (
                      <Button key={coach.email} onClick={() => handleDemoLogin(coach.email)} disabled={demoLoading !== null}
                        className={`w-full justify-start gap-2 h-auto py-1.5 text-white ${isHead ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600/60 hover:bg-green-700/60'}`}
                        data-testid={`demo-coach-${coach.team_id}-btn`}>
                        {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Users className="h-3 w-3" />}
                        <div className="text-left">
                          <div className="text-xs font-medium">{isHead ? 'HC' : 'AC'}: {coach.team_name}</div>
                          <div className="text-[10px] opacity-70">{coach.first_name} {coach.last_name}</div>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-slate-700">
              <p className="text-xs text-slate-500 text-center">
                Demo password: <code className="text-amber-500">Demo2026!</code>
              </p>
            </div>

            {/* Change password — shown when a developer is signed in on /dev
                (their landing page has no dashboard header). */}
            {user && (
              <ChangePasswordDialog
                trigger={
                  <Button
                    variant="ghost"
                    className="w-full text-slate-300 hover:text-white text-xs border border-slate-700 hover:border-amber-500/50 gap-2"
                    data-testid="dev-change-password-btn"
                  >
                    <KeyRound className="h-3.5 w-3.5" /> Change password
                  </Button>
                }
              />
            )}

            {/* "Full Sign Out" — only visible when the developer breadcrumb
                is set in localStorage. This is the ONLY path back to the
                primary /login page, matching AUTH_SPEC.md. */}
            {(() => {
              let hasDev = false;
              try { hasDev = localStorage.getItem('ol_dev_session') === 'true'; } catch {}
              if (!hasDev) return null;
              return (
                <Button
                  variant="ghost"
                  className="w-full text-slate-400 hover:text-red-400 text-xs border border-slate-700 hover:border-red-500/50"
                  onClick={() => {
                    try { localStorage.removeItem('ol_dev_session'); } catch {}
                    window.location.href = '/login';
                  }}
                  data-testid="full-signout-btn"
                >
                  Full Sign Out — Return to primary sign-in
                </Button>
              );
            })()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
